import { defineStore } from 'pinia'
import { subscriptionService } from '@/services/subscriptionService'
import { APP_ROUTES } from '@/const/appRoutes'
import type { Subscription } from '@/types/subscription'
import { handleAuthError, extractErrorMessage } from '@/utils/storeHelpers'

export const useSubscriptionsStore = defineStore('subscriptions', {
  state: () => ({
    subscriptions: [] as Subscription[],
    loading: false,
    error: null as string | null,
    activeSubscription: null as Subscription | null,
    isCreatingSubscription: false,
  }),

  getters: {
    hasSubscriptions: (state) => state.subscriptions.length > 0,
    
    hasActiveSubscription: (state) => !!state.activeSubscription,
    
    isPastDue: (state) => {
      return state.activeSubscription?.status === 'past_due'
    },
    
    isTrialing: (state) => {
      return state.activeSubscription?.status === 'trialing'
    },
    
    isActive: (state) => {
      return state.activeSubscription?.status === 'active'
    },
  },

  actions: {
    async fetchSubscriptions() {
      this.loading = true
      this.error = null

      try {
        const response = await subscriptionService.getCurrentSubscriptions()

        this.subscriptions = response.data

        // Find active subscription
        this.activeSubscription = this.subscriptions.find(sub =>
          ['active', 'trialing'].includes(sub.status)
        ) || null
      } catch (error: any) {
        if (handleAuthError(error)) {
          this.error = 'Current user is not authorized to access subscriptions'
          return
        }

        this.error = extractErrorMessage(error, 'Failed to fetch subscriptions')
      } finally {
        this.loading = false
      }
    },

    async createSubscription(planId: number, trialPeriodDays?: number) {
      this.isCreatingSubscription = true

      try {
        const response = await subscriptionService.createSubscription({
          plan_id: planId,
          trial_period_days: trialPeriodDays
        })

        // Refresh subscriptions
        await this.fetchSubscriptions()
        return response.data

      } catch (error: any) {
        if (handleAuthError(error)) {
          throw new Error('Current user is not authorized to create subscription')
        }

        throw error
      } finally {
        this.isCreatingSubscription = false
      }
    },

    async createCheckoutSession(
      planId: number | string,
      trialPeriodDays?: number,
      opts?: { customerId?: string; paymentMethodId?: string }
    ) {
      this.isCreatingSubscription = true

      try {
        const payload: any = {
          plan_id: planId,
          success_url: `${window.location.origin}${APP_ROUTES.TUITION}?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}${APP_ROUTES.TUITION}?cancelled=true`
        }

        // Add trial_period_days if provided
        if (trialPeriodDays) {
          payload.trial_period_days = trialPeriodDays
        }

        // Optionally include customer/payment method hints for backend
        if (opts?.customerId) {
          payload.customer_id = opts.customerId
        }
        if (opts?.paymentMethodId) {
          payload.payment_method_id = opts.paymentMethodId
        }

        const response = await subscriptionService.createSubscriptionCheckoutSession(payload)
        return response.data

      } catch (error: any) {
        if (handleAuthError(error)) {
          throw new Error('Current user is not authorized to create checkout session')
        }

        throw error
      } finally {
        this.isCreatingSubscription = false
      }
    },

    async retrieveCheckoutSession(sessionId: string) {
      try {
        const response = await subscriptionService.getCheckoutSession(sessionId)

        // Refresh subscriptions after successful checkout
        if (response.data.status === 'complete') {
          await this.fetchSubscriptions()
        }

        return response.data
      } catch (error: any) {
        if (handleAuthError(error)) {
          throw new Error('Current user is not authorized to retrieve checkout session')
        }

        throw error
      }
    },

    async retryPayment() {
      try {
        const response = await subscriptionService.retryPayment()
        return response.data
      } catch (error: any) {
        if (handleAuthError(error)) {
          throw new Error('Current user is not authorized to retry payment')
        }

        throw error
      }
    },
  }
})

